import { useEffect, useState } from 'preact/compat'
import { useDispatch, useSelector } from 'react-redux'
import { paymentMethodsMap } from '../components/MfeElements/PaymentMethods/paymentMethodsMap'
import { PAYMENT_METHOD_TYPES } from '../constants/paymentMethods'
import { pageStateSelector } from '../redux/slice/pageStateSlice'
import {
  transactionDetailsSelector,
  transactionDetailsUpdate,
} from '../redux/slice/transactionDetailsSlice'
import { DataDog } from '../datadog'

export default function usePaymentMethods({
  availableTenantPaymentMethods,
  selectedTenantPaymentMethod,
}) {
  const dispatch = useDispatch()
  const transactionDetails = useSelector(transactionDetailsSelector)
  const pageState = useSelector(pageStateSelector)
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState()

  // Handle changes in MFE payment methods attribute
  useEffect(() => {
    const { availablePaymentMethods } = transactionDetails
    const tenantPaymentMethods = availableTenantPaymentMethods?.split(',')
    const isTenantPaymentMethodsValid = tenantPaymentMethods
      ? tenantPaymentMethods.every(item => paymentMethodsMap.has(item))
      : false

    // Check if Tenant's payment methods are valid
    if (isTenantPaymentMethodsValid) {
      dispatch(
        transactionDetailsUpdate({
          tenantPaymentMethods,
        }),
      )
    } else {
      // if no - just reset them to default that we got from BE
      if (tenantPaymentMethods?.length && tenantPaymentMethods.some(item => item)) {
        const invalidMethods = tenantPaymentMethods.filter(
          item => !paymentMethodsMap.has(item),
        )
        DataDog.warn(
          'Tenant payment methods (MFE attributes) contain entries not in paymentMethodsMap, falling back to BE defaults',
          {
            invalidMethods,
            providedMethods: tenantPaymentMethods,
            fallbackMethods: availablePaymentMethods,
          },
        )
        DataDog.info('Payment methods resolved after fallback', {
          resolvedMethods: availablePaymentMethods,
        })
      }

      dispatch(
        transactionDetailsUpdate({
          tenantPaymentMethods: availablePaymentMethods,
        }),
      )
    }
  }, [
    availableTenantPaymentMethods,
    selectedTenantPaymentMethod,
    transactionDetails.availablePaymentMethods,
  ])

  // Handle changes in MFE selected payment method attribute
  useEffect(() => {
    let { defaultPaymentMethod } = transactionDetails

    // If ApplePay available as Payment Method - preselect it and
    // ignore other overrides
    if (
      pageState.displayedPaymentMethods.includes(PAYMENT_METHOD_TYPES.ApplePay)
    ) {
      defaultPaymentMethod = PAYMENT_METHOD_TYPES.ApplePay
      dispatch(
        transactionDetailsUpdate({
          tenantSelectedPaymentMethod: defaultPaymentMethod,
        }),
      )
      setCurrentPaymentMethod(defaultPaymentMethod)

      return
    }

    // Tenant overrides selected-payment-method attribute with payment method
    if (
      selectedTenantPaymentMethod &&
      pageState.displayedPaymentMethods.length
    ) {
      const isTenantSelectedMethodValid =
        pageState.displayedPaymentMethods.includes(selectedTenantPaymentMethod)

      // Check if Tenant's selected payment method is valid
      if (isTenantSelectedMethodValid) {
        dispatch(
          transactionDetailsUpdate({
            tenantSelectedPaymentMethod: selectedTenantPaymentMethod,
          }),
        )

        setCurrentPaymentMethod(selectedTenantPaymentMethod)
      } else {
        // If Tenant's selected payment method is NOT valid

        const isDefaultPaymentMethodExistsInList =
          pageState.displayedPaymentMethods.includes(defaultPaymentMethod)

        // Check if default payment method from BE exists in tenant payment methods list
        if (isDefaultPaymentMethodExistsInList) {
          dispatch(
            transactionDetailsUpdate({
              tenantSelectedPaymentMethod: defaultPaymentMethod,
            }),
          )
          setCurrentPaymentMethod(defaultPaymentMethod)
        } else {
          // Set the first available payment method from payment methods list
          dispatch(
            transactionDetailsUpdate({
              tenantSelectedPaymentMethod: pageState.displayedPaymentMethods[0],
            }),
          )
          setCurrentPaymentMethod(pageState.displayedPaymentMethods[0])
        }
      }
    } else if (defaultPaymentMethod) {
      // if Tenant didn't specify selected-payment-method we set it to default from BE
      const isDefaultPaymentMethodExistsInList =
        transactionDetails.tenantPaymentMethods.includes(defaultPaymentMethod)

      if (isDefaultPaymentMethodExistsInList) {
        dispatch(
          transactionDetailsUpdate({
            tenantSelectedPaymentMethod: defaultPaymentMethod,
          }),
        )
        setCurrentPaymentMethod(defaultPaymentMethod)
      } else {
        // Set the first available payment method from payment methods list
        dispatch(
          transactionDetailsUpdate({
            tenantSelectedPaymentMethod: pageState.displayedPaymentMethods[0],
          }),
        )
        setCurrentPaymentMethod(pageState.displayedPaymentMethods[0])
      }
    } else {
      // if /details BE response doesn't include default payment method take the first one from the list
      dispatch(
        transactionDetailsUpdate({
          tenantSelectedPaymentMethod: pageState.displayedPaymentMethods[0],
        }),
      )

      setCurrentPaymentMethod(pageState.displayedPaymentMethods[0])
    }
  }, [
    availableTenantPaymentMethods,
    selectedTenantPaymentMethod,
    transactionDetails.tenantPaymentMethods,
    pageState.displayedPaymentMethods,
    transactionDetails.defaultPaymentMethod,
  ])

  return {
    currentPaymentMethod,
    setCurrentPaymentMethod,
  }
}
